import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { projectsApi } from "@services/api";
import { billingTemplatesApi, BillingTemplate } from "@services/api/billingTemplates";
import { Project, ProjectBillingStep } from "types";
import { navigateTo } from "context/navigation.service";
import Icon1 from "@assets/icons/Icon";
import DataGrid from "@components/ui/tables/DataGrid";
import { PDFViewerModal } from "@components/modals/PDFViewerModal";
import { searchProjectsFull } from "@services/algolia";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { orgMembersToUsers, MemberAssignmentItem } from "@components/ui/blocks/MemberAssignmentBlock";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";

function StepCell({ step, onOpenPdf, onResend, resending, daysUntilEnd, prevSentAt }: {
  step: ProjectBillingStep | null;
  onOpenPdf?: (url: string, title: string) => void;
  onResend?: () => void;
  resending?: boolean;
  daysUntilEnd?: number | null;
  prevSentAt?: string | null;
}) {
  if (!step) return <span className="text-h5 text-neutral-300">—</span>;

  const hasPdf = !!(step.invoice_pdf_url && step.invoice_pdf_url.startsWith("http"));
  const sentDate = step.sent_at ? new Date(step.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "";

  const isLate = (() => {
    if (!step.sent_at || step.status === "paid") return false;
    const delay = step.payment_delay_days ?? 15;
    return (new Date().getTime() - new Date(step.sent_at).getTime()) / (1000 * 60 * 60 * 24) > delay;
  })();

  const isProjectDelay = step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days;

  // Step gap delay
  const isStepGap = step.step_gap_delay_days && ["pending", "draft", "finalized"].includes(step.status) && prevSentAt && (() => {
    const daysSincePrev = (Date.now() - new Date(prevSentAt).getTime()) / (1000 * 60 * 60 * 24);
    return daysSincePrev > step.step_gap_delay_days!;
  })();

  const wrap = (content: React.ReactNode, className: string) =>
    hasPdf ? (
      <span
        className={`inline-block px-2 py-0.5 rounded text-h5 ${className} cursor-pointer`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenPdf?.(step.invoice_pdf_url!, step.label);
        }}>
        {content}
      </span>
    ) : (
      <span className={`inline-block px-2 py-0.5 rounded text-h5 ${className}`}>{content}</span>
    );

  // Step gap retard (applies to pending/draft/finalized)
  if (isStepGap && ["pending", "draft", "finalized"].includes(step.status)) {
    const daysLate = Math.floor((Date.now() - new Date(prevSentAt!).getTime()) / (1000 * 60 * 60 * 24)) - step.step_gap_delay_days!;
    return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-red-100 text-red-700">
      Retard étape · +{daysLate}j
    </span>;
  }

  switch (step.status) {
    case "paid":
      return wrap("Payée", "bg-emerald-100 text-emerald-700 hover:bg-emerald-200");
    case "sent": {
      // Surface only the manual relance timestamp here — the scheduler's
      // conductor alerts are not customer-facing and would mislabel a
      // Sunday cron run as a Sunday relance otherwise.
      const alertDate = step.last_relance_sent_at
        ? new Date(step.last_relance_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
        : null;
      return (
        <span className="inline-flex items-center gap-1" style={{ whiteSpace: "nowrap" }}>
          {isLate
            ? wrap(`En retard · ${sentDate}`, "bg-red-100 text-red-700 hover:bg-red-200")
            : wrap(`Envoyée · ${sentDate}`, "bg-blue-100 text-blue-700 hover:bg-blue-200")}
          {onResend && (
            <button
              className={`flex-shrink-0 p-0.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 ${resending ? "animate-spin" : ""}`}
              title={alertDate ? `Relance demandée le ${alertDate}` : "Demander une relance au comptable"}
              disabled={resending}
              onClick={(e) => { e.stopPropagation(); onResend(); }}
            >
              <Icon1 name="sync" size={13} />
            </button>
          )}
          {alertDate && (
            <span className="inline-block px-1.5 py-0.5 rounded text-h6 bg-orange-50 text-orange-600" title={`Relance demandée le ${alertDate}`}>
              ↻ {alertDate}
            </span>
          )}
        </span>
      );
    }
    case "finalized":
      return wrap("Finalisée", "bg-blue-50 text-blue-600 hover:bg-blue-100");
    case "draft":
      return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-amber-50 text-amber-600">Brouillon</span>;
    case "creating":
      return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-amber-50 text-amber-500 animate-pulse">Création...</span>;
    case "error":
      return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-red-50 text-red-500">Erreur</span>;
    case "pending":
      if (isProjectDelay) {
        return <span className="inline-block px-2 py-0.5 rounded text-h5 bg-red-100 text-red-700">
          Retard · J-{daysUntilEnd}
        </span>;
      }
      return <span className="text-h5 text-neutral-300">—</span>;
    default:
      return <span className="text-h5 text-neutral-300">—</span>;
  }
}

const PAGE_SIZE = 50;

export default function FacturationScreen() {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const [billingTemplates, setBillingTemplates] = useState<BillingTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("en_cours");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Role-based access: admin/owner always see all, + users in billing_full_access_user_ids
  const billingFullAccessIds = orgData?.config?.billing_full_access_user_ids || [];
  const hasFullAccess = isOwnerOrAdmin(userData) || (userData?.id ? billingFullAccessIds.includes(userData.id) : false);
  // A conducteur only works on their own book — the "filter by conducteur"
  // dropdown makes no sense for them and the "Conducteur" column would
  // just repeat their own name on every row. Hide both regardless of
  // billing access level.
  const isConductor = userData?.role === "conductor";
  const canFilterByConductor = hasFullAccess && !isConductor;
  // "Demander une relance" doesn't actually resend the invoice — it just
  // emails the configured comptables (billing_cc_user_ids) that the bill
  // needs a follow-up. Every user who can reach this page is allowed to
  // trigger that alert, conducteurs included. Guests + dessinateurs are
  // already blocked at the route level (navigation/index.tsx), and the
  // backend enforces "not a guest" + "same org" on the endpoint itself.
  const canResend = true;
  const [resendingKey, setResendingKey] = useState<string | null>(null);

  const handleResend = useCallback(async (projectId: string, stepIndex: number) => {
    const key = `${projectId}-${stepIndex}`;
    setResendingKey(key);
    try {
      const updated = await projectsApi.resendBillingInvoice(projectId, stepIndex);
      setProjects((prev) => prev.map((p) => p.id === projectId ? updated : p));
      showNotification({ type: "success", message: "Demande de relance envoyée au comptable" });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showNotification({ type: "error", message: typeof detail === "string" ? detail : "Erreur lors de la demande de relance" });
    } finally {
      setResendingKey(null);
    }
  }, []);

  // Conductor filter (admin view only)
  const conductors = orgMembers.filter(
    (m) => m.role === "conductor" && m.status !== "inactive",
  );
  const [filterConductorId, setFilterConductorId] = useState<string>("");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Close filter on outside click
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node) &&
          filterBtnRef.current && !filterBtnRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const [pdfModal, setPdfModal] = useState<{ url: string; title: string } | null>(null);
  const openPdf = useCallback((url: string, title: string) => {
    if (url && url.startsWith("http")) {
      setPdfModal({ url, title });
    }
  }, []);

  // Fetch billing templates and auto-select all of them
  useEffect(() => {
    if (!userData?.org_id) return;
    billingTemplatesApi.list(userData.org_id).then((data) => {
      setBillingTemplates(data);
      setSelectedTemplateIds(data.map((t) => t.id));
    }).catch(() => {});
  }, [userData?.org_id]);

  // Determine which conductor to filter by.
  //
  // - Admins / billing_full_access_user_ids: optional dropdown filter.
  // - Other non-admins: no explicit conductor filter — let the backend's
  //   "restrict_to_team_for_user" scope the list to every project they're
  //   on (as conductor, commercial, SAV, dessinateur, etc. — not only
  //   those where they happen to be the lead conductor).
  const effectiveConductorId = hasFullAccess
    ? filterConductorId || undefined
    : undefined;

  const selectedTemplateIdParam = selectedTemplateIds.join(",");

  // Fetch projects when template or filter changes
  const fetchProjects = useCallback(async (newOffset = 0) => {
    if (!selectedTemplateIdParam || !userData?.org_id) {
      setProjects([]);
      return;
    }
    setIsLoading(true);
    setOffset(newOffset);
    try {
      const data = await projectsApi.list({
        org_id: userData.org_id,
        billing_template_id: selectedTemplateIdParam,
        conductor_id: effectiveConductorId,
        limit: PAGE_SIZE,
        offset: newOffset,
      });
      setProjects(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTemplateIdParam, userData?.org_id, effectiveConductorId]);

  useEffect(() => {
    fetchProjects(0);
  }, [fetchProjects]);


  // Sync with Pennylane (lazy — only current page)
  const handleSync = useCallback(async () => {
    if (!selectedTemplateIdParam) return;
    setSyncing(true);
    try {
      const data = await projectsApi.syncAllBilling(selectedTemplateIdParam);
      const filtered = effectiveConductorId
        ? data.filter((p) => p.infos?.team?.conductor_id === effectiveConductorId)
        : data;
      setProjects(filtered);
    } catch {}
    setSyncing(false);
  }, [selectedTemplateIdParam, effectiveConductorId]);

  const selectedTemplates = billingTemplates.filter((b) => selectedTemplateIds.includes(b.id));
  const hasDemarrage = selectedTemplates.some((t) => t.steps.some((s) => s.is_demarrage));

  // Anchor on the template with the MOST avancement steps so we never lose columns.
  // Templates with fewer steps simply leave their trailing columns empty per project.
  const anchorTemplate = useMemo(() => {
    if (selectedTemplates.length === 0) return null;
    return selectedTemplates.reduce((best, t) => {
      const count = t.steps.filter((s) => !s.is_demarrage).length;
      const bestCount = best ? best.steps.filter((s) => !s.is_demarrage).length : -1;
      return count > bestCount ? t : best;
    }, selectedTemplates[0]);
  }, [selectedTemplates]);

  const avancementTemplateSteps = anchorTemplate?.steps.filter((s) => !s.is_demarrage) ?? [];
  const maxAvancementCount = avancementTemplateSteps.length;

  // Compute reconciled column headers across all selected templates.
  // For each column index: if all templates that have a step at that index agree
  // on the abbreviation/label, use it. Otherwise use a generic "Facture N" with a
  // tooltip listing each template's specific label.
  const reconciledHeaders = useMemo(() => {
    if (selectedTemplates.length <= 1) return null;
    const allAvancement = selectedTemplates.map((t) => t.steps.filter((s) => !s.is_demarrage));
    const headers: { label: string; tooltip?: string }[] = [];
    for (let i = 0; i < maxAvancementCount; i++) {
      const labelsByTemplate = selectedTemplates.map((t, ti) => ({
        template: t.name,
        label: allAvancement[ti][i]?.abbreviation || allAvancement[ti][i]?.label || null,
      }));
      const present = labelsByTemplate.filter((x) => x.label);
      const unique = Array.from(new Set(present.map((x) => x.label!)));
      if (unique.length === 1) {
        headers.push({ label: unique[0] });
      } else {
        const tooltipLines = present.map((x) => `${x.template}: ${x.label}`).join(" / ");
        headers.push({ label: `Facture ${i + 1}`, tooltip: tooltipLines });
      }
    }
    return headers;
  }, [selectedTemplates, maxAvancementCount]);

  // Split projects into active and archived (fully paid)
  const isFullyPaid = (p: Project) => {
    if (!p.billing?.steps.length) return false;
    return p.billing.steps.every((s) => s.status === "paid");
  };

  const activeProjects = projects.filter((p) => !isFullyPaid(p));
  const archivedProjects = projects.filter(isFullyPaid);
  const displayedProjects = activeTab === "en_cours" ? activeProjects : archivedProjects;

  // Stats (TTC = HT * 1.2)
  // Prefer pre-computed billing_flags (recomputed by backend on changes + daily cron).
  // Falls back to client-side computation for projects that haven't been flagged yet.
  const TVA = 1.2;
  const totalMontant = projects.reduce((sum, p) => sum + (p.billing?.montant_total || 0), 0);
  const totalInvoiced = projects.reduce((sum, p) => {
    const flags = p.billing?.billing_flags;
    if (flags) return sum + flags.total_invoiced;
    return (
      sum +
      (p.billing?.steps || [])
        .filter((s) => ["finalized", "sent", "paid"].includes(s.status))
        .reduce((s2, st) => s2 + st.montant, 0)
    );
  }, 0);
  const totalPaid = projects.reduce((sum, p) => {
    const flags = p.billing?.billing_flags;
    if (flags) return sum + flags.total_paid;
    return (
      sum +
      (p.billing?.steps || [])
        .filter((s) => s.status === "paid")
        .reduce((s2, st) => s2 + st.montant, 0)
    );
  }, 0);
  const lateCount = projects.reduce((sum, p) => {
    const flags = p.billing?.billing_flags;
    return sum + (flags?.overdue_count || 0);
  }, 0);

  // Build DataGrid table
  const table = useMemo(() => {
    if (!anchorTemplate) return null;

    const columns: any[] = [
      {
        field: "name",
        headerName: "Projet",
        flex: 1,
        renderCell: ({ row }: any) => (
          <div className="flex flex-col cursor-pointer" onClick={(e) => { e.stopPropagation(); navigateTo({ route: `/project/${row.id}?tab=facturation` }); }}>
            <span className="text-h3 font-medium text-neutral-800 hover:underline">{row.name}</span>
            <span className="text-h4 text-neutral-400">{row.city}{row.num ? ` · ${row.num}` : ""}</span>
          </div>
        ),
      },
    ];

    if (canFilterByConductor && !filterConductorId) {
      columns.push({
        field: "conductor",
        headerName: "Conducteur",
        width: 120,
        renderCell: ({ row }: any) => (
          <span className="text-h4 text-neutral-500">{row.conductor || "—"}</span>
        ),
      });
    }

    const colWidth = (label: string) => Math.max(120, Math.min(label.length * 8 + 30, 220));

    if (hasDemarrage) {
      columns.push({
        field: "demarrage",
        headerName: "Démarrage",
        width: colWidth("Démarrage"),
        renderCell: ({ row }: any) => <StepCell step={row.demarrage} onOpenPdf={openPdf} daysUntilEnd={row._daysUntilEnd}
          onResend={canResend && row.demarrage?.status === "sent" && row._demarrageIdx != null ? () => handleResend(row.id, row._demarrageIdx) : undefined}
          resending={resendingKey === `${row.id}-${row._demarrageIdx}`} />,
      });
    }

    avancementTemplateSteps.forEach((s, i) => {
      const rh = reconciledHeaders?.[i];
      const headerLabel = rh ? rh.label : (s.abbreviation || s.label);
      const headerTooltip = rh?.tooltip;
      columns.push({
        field: `step_${i}`,
        headerName: headerLabel,
        headerTooltip,
        width: colWidth(headerLabel),
        renderCell: ({ row }: any) => <StepCell step={row[`step_${i}`]} onOpenPdf={openPdf} daysUntilEnd={row._daysUntilEnd} prevSentAt={row[`_prevSent_${i}`]}
          onResend={canResend && row[`step_${i}`]?.status === "sent" && row[`_stepIdx_${i}`] != null ? () => handleResend(row.id, row[`_stepIdx_${i}`]) : undefined}
          resending={resendingKey === `${row.id}-${row[`_stepIdx_${i}`]}`} />,
      });
    });

    columns.push({
      field: "total",
      headerName: "Total TTC",
      width: 100,
      renderCell: ({ row }: any) => (
        <span className="text-h3 font-medium text-neutral-700">{row.total} €</span>
      ),
    });

    const rows = displayedProjects.map((project) => {
      const billing = project.billing;
      if (!billing) return null;

      const demarrageSteps = billing.steps.filter((s) => s.is_demarrage);
      const avancementSteps = billing.steps.filter((s) => !s.is_demarrage);

      const conductorId = project.infos?.team?.conductor_id;
      const conductorMember = conductorId ? conductors.find((c) => c.id === conductorId) : null;

      // Days until project end (from cached planning_end_date)
      const endDateStr = billing.planning_end_date;
      const daysUntilEnd = endDateStr ? Math.round((new Date(endDateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

      const row: any = {
        id: project.id,
        name: project.infos?.general?.name || project.id,
        city: project.infos?.general?.city || "",
        num: project.infos?.general?.num || "",
        conductor: conductorMember?.infos?.display_name || "",
        total: Math.round(billing.montant_total * TVA).toLocaleString("fr-FR"),
        _daysUntilEnd: daysUntilEnd,
      };

      if (hasDemarrage) {
        const firstDemarrageOrigIdx = billing.steps.findIndex((s) => s.is_demarrage);
        row.demarrage = demarrageSteps.length > 0 ? {
          ...demarrageSteps[0],
          montant: demarrageSteps.reduce((sum, s) => sum + s.montant, 0),
        } : null;
        row._demarrageIdx = firstDemarrageOrigIdx >= 0 ? firstDemarrageOrigIdx : null;
      }

      avancementSteps.forEach((step, i) => {
        row[`step_${i}`] = step;
        // Store original billing step index for resend
        const origIdx = billing.steps.findIndex((s) => s === step);
        row[`_stepIdx_${i}`] = origIdx >= 0 ? origIdx : null;
        // Find previous step's sent_at for step gap detection
        if (i > 0) {
          const prev = avancementSteps[i - 1];
          if (prev.sent_at && ["sent", "paid"].includes(prev.status)) {
            row[`_prevSent_${i}`] = prev.sent_at;
          }
        }
      });

      return row;
    }).filter(Boolean);

    return { columns, rows };
  }, [displayedProjects, anchorTemplate, hasDemarrage, avancementTemplateSteps, reconciledHeaders, openPdf, canResend, handleResend, resendingKey, canFilterByConductor, filterConductorId]);

  const tabs = [
    { value: "en_cours", label: `En cours (${activeProjects.length})` },
    { value: "archives", label: `Archivés (${archivedProjects.length})` },
  ];

  return (
    <div className="h-screen flex flex-col overflow-auto">
      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col w-full max-w-1p2k">
          <div className="flex flex-col mb-4">
            <Text1>Suivi de facturation</Text1>
            <Text2>Vue d'ensemble de la facturation de tous les projets.</Text2>
          </div>

          {/* Toolbar: sync + conductor filter */}
          <div className="flex flex-row gap-2 flex-wrap items-center">
            <button
              onClick={handleSync}
              disabled={syncing || selectedTemplateIds.length === 0}
              className="flex items-center gap-1.5 text-h4 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
            >
              <Icon1 name="refresh" size={14} />
              {syncing ? "Synchronisation..." : "Synchroniser"}
            </button>

            {/* Conductor filter — shown only to users who actually manage
                multiple conducteurs' books (admins + billing-full-access).
                Conducteurs themselves never see this filter. */}
            {canFilterByConductor && (
              <>
                <button
                  ref={filterBtnRef}
                  onClick={() => setFilterOpen(!filterOpen)}
                  className={`inline-flex items-center gap-1 px-3 py-2 border rounded-lg text-h4 ml-2 ${
                    filterConductorId
                      ? "border-neutral-800 text-neutral-800 bg-neutral-100"
                      : "border-neutral-300 text-neutral-500 hover:bg-neutral-50"
                  }`}
                >
                  <Icon1 name="expert" size={14} />
                  {filterConductorId
                    ? conductors.find((c) => c.id === filterConductorId)?.infos?.display_name || "Conducteur"
                    : "Conducteur"}
                  {filterConductorId && (
                    <span
                      className="ml-1 text-h5 text-neutral-400 hover:text-neutral-600"
                      onClick={(e) => { e.stopPropagation(); setFilterConductorId(""); }}
                    >
                      ✕
                    </span>
                  )}
                </button>
                {filterOpen && createPortal(
                  <div
                    ref={filterDropdownRef}
                    style={{
                      position: "fixed",
                      top: (filterBtnRef.current?.getBoundingClientRect().bottom || 0) + 4,
                      left: filterBtnRef.current?.getBoundingClientRect().left || 0,
                    }}
                    className="z-50 bg-white border rounded-lg shadow-lg p-2 min-w-200 max-h-300 overflow-auto"
                  >
                    <div
                      className={`px-3 py-1.5 rounded text-h4 cursor-pointer ${!filterConductorId ? "bg-neutral-100 font-medium" : "hover:bg-neutral-50"}`}
                      onClick={() => { setFilterConductorId(""); setFilterOpen(false); }}
                    >
                      Tous les conducteurs
                    </div>
                    {conductors.map((c) => (
                      <div
                        key={c.id}
                        className={`px-3 py-1.5 rounded text-h4 cursor-pointer ${filterConductorId === c.id ? "bg-neutral-100 font-medium" : "hover:bg-neutral-50"}`}
                        onClick={() => { setFilterConductorId(c.id); setFilterOpen(false); }}
                      >
                        {c.infos?.display_name || c.infos?.email || c.id}
                      </div>
                    ))}
                  </div>,
                  document.body,
                )}
              </>
            )}
          </div>

          {/* Non-admin info */}
          {!hasFullAccess && (
            <div className="text-h4 text-neutral-400 mt-2">
              Vous voyez uniquement les projets dont vous êtes conducteur de travaux.
            </div>
          )}
        </div>
      </div>

      {selectedTemplateIds.length > 0 && (
        <>
          {/* Summary cards */}
          <div className="flex-shrink-0 px-4 lg:px-10 pb-4">
            <div className="flex flex-row gap-3 max-w-1p2k">
              {isLoading ? (
                <>
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex-1 bg-white rounded-lg border px-4 py-3">
                      <div className="h-3 w-16 bg-neutral-100 rounded animate-pulse mb-2" />
                      <div className="h-5 w-20 bg-neutral-100 rounded animate-pulse" />
                    </div>
                  ))}
                </>
              ) : (
                <>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Projets</div>
                <div className="text-h1 font-semibold text-neutral-800">{projects.length}</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Montant total TTC</div>
                <div className="text-h1 font-semibold text-neutral-800">{Math.round(totalMontant * TVA).toLocaleString("fr-FR")} €</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Facturé</div>
                <div className="text-h1 font-semibold text-emerald-600">{Math.round(totalInvoiced * TVA).toLocaleString("fr-FR")} €</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Encaissé</div>
                <div className="text-h1 font-semibold text-emerald-700">{Math.round(totalPaid * TVA).toLocaleString("fr-FR")} €</div>
              </div>
              <div className="flex-1 bg-white rounded-lg border px-4 py-3">
                <div className="text-h4 text-neutral-400">Restant</div>
                <div className="text-h1 font-semibold text-amber-600">{Math.round((totalMontant - totalInvoiced) * TVA).toLocaleString("fr-FR")} €</div>
              </div>
              {lateCount > 0 && (
                <div className="flex-1 bg-white rounded-lg border border-red-200 px-4 py-3">
                  <div className="text-h4 text-neutral-400">En retard</div>
                  <div className="text-h1 font-semibold text-red-600">{lateCount}</div>
                </div>
              )}
                </>
              )}
            </div>
          </div>

          {/* DataGrid */}
          <div className="px-4 lg:px-10 pb-6 flex flex-col w-full">
            {syncing && !isLoading && (
              <div className="flex items-center gap-2 text-h4 text-neutral-400 mb-2">
                <div className="w-3 h-3 border-2 border-neutral-300 border-t-neutral-500 rounded-full animate-spin" />
                Synchronisation avec Pennylane...
              </div>
            )}
            {isLoading ? (
              <div className="flex flex-col gap-2 max-w-1p2k">
                <div className="h-10 rounded-lg bg-neutral-100 animate-pulse" />
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-12 rounded-lg bg-neutral-50 animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
                ))}
              </div>
            ) : table ? (
              <div className="max-w-1p2k w-full" style={{ minHeight: 500 }}>
                <DataGrid
                  table={table}
                  hide_lines={true}
                  initialPageSize={10}
                  tabs={tabs}
                  activeTab={activeTab}
                  onTabChange={(tab) => setActiveTab(tab)}
                  onSearch={
                    orgData?.id
                      ? (q) => searchProjectsFull(q, orgData.id)
                      : undefined
                  }
                  onSearchSelect={(option) =>
                    navigateTo({ route: `/project/${option.value}?tab=facturation` })
                  }
                  hasNext={hasMore}
                  hasPrev={offset > 0}
                  onNext={() => fetchProjects(offset + PAGE_SIZE)}
                  onPrev={() => fetchProjects(Math.max(0, offset - PAGE_SIZE))}
                  storageKey="facturation"
                />
              </div>
            ) : (
              <div className="text-h3 text-neutral-400 py-12 text-center">
                Aucun projet avec ce modèle de facturation.
              </div>
            )}
          </div>
        </>
      )}

      {pdfModal && (
        <PDFViewerModal
          pdfUrl={pdfModal.url}
          title={`Facture — ${pdfModal.title}`}
          onClose={() => setPdfModal(null)}
        />
      )}
    </div>
  );
}
